import React, { useState, useEffect, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const BetaHeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [parallaxY, setParallaxY] = useState(0);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;
    const handleScroll = () => setParallaxY(window.scrollY * 0.25);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToFeedback = () => {
    document.getElementById('feedback-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToBetaActions = () => {
    document.getElementById('beta-actions')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative py-16 md:py-32 flex items-center justify-center overflow-hidden min-h-[70vh]">
      {/* Background Layer 1: image2.jpg — static, full presence */}
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="/image2.jpg"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-center opacity-50 blur-[1px] scale-105"
        />
      </div>

      {/* Background Layer 2: Light gradient overlay */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-background/40 via-background/20 to-background/70" />

      {/* Background Layer 3: cosmic tint + particles */}
      <div className="absolute inset-0 cosmic-gradient pointer-events-none" style={{ opacity: 0.35 }}>
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(12)]?.map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-primary/30 rounded-full animate-pulse"
              style={{
                left: `${(i * 8.3) % 100}%`,
                top: `${(i * 7.7) % 100}%`,
                animationDelay: `${i * 0.25}s`,
                animationDuration: `${3 + (i % 3)}s`
              }}
            />
          ))}
        </div>
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-accent/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
        {/* Logo */}
        <div className={`mb-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="relative inline-block">
            <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl scale-150 animate-pulse" />
            <Image
              src="/brand/depthme-icon-192.png"
              alt="DepthMe AI journaling app logo"
              className="relative w-16 h-16 mx-auto object-contain drop-shadow-2xl"
            />
          </div>
        </div>

        {/* Badge */}
        <div className={`mb-6 transition-all duration-1000 delay-150 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-flex items-center space-x-2 glassmorphism px-4 py-1.5 rounded-full text-sm font-medium text-accent border border-accent/30">
            <div className="w-2 h-2 bg-accent rounded-full animate-pulse" />
            <span>Beta is open</span>
          </span>
        </div>

        {/* Headline */}
        <div className={`space-y-4 mb-8 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h1 className="text-4xl md:text-6xl font-bold text-foreground leading-tight">
            Try the{' '}
            <span className="text-gradient">DepthMe Beta</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Mind reframing + your own voice + daily rituals built around your real situation. The first app that speaks your guidance back to you in your own voice.
          </p>
        </div>

        {/* CTA Buttons */}
        <div className={`flex flex-col sm:flex-row items-center justify-center gap-3 mb-5 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Button
            variant="default"
            size="lg"
            onClick={() => window.open('https://depthme.app', '_blank', 'noopener,noreferrer')}
            className="violet-glow px-8 py-3 w-full sm:w-auto"
            iconName="ArrowRight"
            iconPosition="right"
          >
            Open the App
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={scrollToFeedback}
            className="px-8 py-3 w-full sm:w-auto"
            iconName="MessageSquare"
            iconPosition="left"
          >
            Give Feedback
          </Button>
        </div>

        {/* Support text */}
        <p className={`text-sm text-muted-foreground transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          Help us test, improve, and shape the first public version of DepthMe.
        </p>

        {/* Incentive line */}
        <div className={`mt-4 transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-sm font-medium bg-gradient-to-r from-accent via-primary to-accent bg-clip-text text-transparent">
            ✨ Early beta testers will receive 1 full year of DepthMe Premium when we launch.
          </p>
        </div>

        {/* Spots remaining — hidden until count is large enough to be compelling
        {spotsRemaining !== null && (
          <div className={`mt-3 transition-all duration-1000 delay-800 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <p className="text-xs text-muted-foreground">
              Only <span className="text-accent font-semibold">{spotsRemaining}</span> spots remaining
            </p>
          </div>
        )}
        */}
      </div>
    </section>
  );
};

export default BetaHeroSection;